import React, { useState } from "react";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import { useToast } from "../../contexts/ToastContext";
import { useSubmitSupportRequestMutation } from "../../store/api/supportApi";

const HelpPage: React.FC = () => {
	const { success, error: showError } = useToast();
	const [submitSupportRequest, { isLoading: isSubmitting }] =
		useSubmitSupportRequestMutation();
	const [contactForm, setContactForm] = useState({
		subject: "",
		message: "",
	});

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		try {
			const response = await submitSupportRequest({
				subject: contactForm.subject,
				message: contactForm.message,
			}).unwrap();
			success(
				`${response.message} (Notified ${response.admins_notified} administrator(s))`,
			);
			setContactForm({ subject: "", message: "" });
		} catch (error: any) {
			const errorMessage =
				error?.data?.detail ||
				error?.data?.message ||
				"Failed to submit support request. Please try again.";
			showError(errorMessage);
		}
	};

	const faqs = [
		{
			question: "How do I add a new property?",
			answer:
				"Navigate to Property Management and click the 'Add Property' button. Fill in the property details and upload images.",
		},
		{
			question: "How do I create a lease?",
			answer:
				"Go to Lease Management, click 'Create Lease', select a tenant and property, then fill in the lease details.",
		},
		{
			question: "How do I process a payment?",
			answer:
				"Navigate to Payment Management, find the payment record, and mark it as paid. You can also upload payment proof.",
		},
		{
			question: "How do I assign a maintenance request?",
			answer:
				"Go to Maintenance Management, open a request, and assign it to a technician from the dropdown menu.",
		},
		{
			question: "How do I add a new user?",
			answer:
				"Navigate to User Management, click 'Add User', fill in their details, select a role, and the system will send them login credentials.",
		},
	];

	const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

	return (
		<div className="w-full max-w-4xl mx-auto">
			{/* Header */}
			<div className="mb-6">
				<h1 className="text-2xl font-bold text-foreground">Help & Support</h1>
				<p className="text-muted-foreground mt-1">
					Get help with using Dofarahub
				</p>
			</div>

			{/* Quick Links */}
			<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
				<a
					href="mailto:support@dofara.com"
					className="bg-card border border-border rounded-lg p-4 hover:border-primary transition-colors"
				>
					<Icon name="Mail" size={24} className="text-primary mb-2" />
					<h3 className="font-semibold text-foreground">Email Support</h3>
					<p className="text-sm text-muted-foreground mt-1">
						support@dofara.com
					</p>
				</a>

				<a
					href="tel:+1234567890"
					className="bg-card border border-border rounded-lg p-4 hover:border-primary transition-colors"
				>
					<Icon name="Phone" size={24} className="text-primary mb-2" />
					<h3 className="font-semibold text-foreground">Phone Support</h3>
					<p className="text-sm text-muted-foreground mt-1">
						+1 (234) 567-8900
					</p>
				</a>

				<div className="bg-card border border-border rounded-lg p-4">
					<Icon name="Clock" size={24} className="text-primary mb-2" />
					<h3 className="font-semibold text-foreground">Business Hours</h3>
					<p className="text-sm text-muted-foreground mt-1">Mon-Fri: 9AM-5PM</p>
				</div>
			</div>

			{/* FAQs */}
			<div className="bg-card border border-border rounded-lg p-6 mb-6">
				<h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
					<Icon name="HelpCircle" size={20} />
					Frequently Asked Questions
				</h2>

				<div className="space-y-2">
					{faqs.map((faq, index) => (
						<div
							key={index}
							className="border border-border rounded-lg overflow-hidden"
						>
							<button
								onClick={() =>
									setExpandedFaq(expandedFaq === index ? null : index)
								}
								className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-muted transition-colors"
							>
								<span className="font-medium text-foreground">
									{faq.question}
								</span>
								<Icon
									name="ChevronDown"
									size={16}
									className={`transition-transform ${
										expandedFaq === index ? "rotate-180" : ""
									}`}
								/>
							</button>
							{expandedFaq === index && (
								<div className="px-4 py-3 bg-muted/50 border-t border-border">
									<p className="text-sm text-foreground">{faq.answer}</p>
								</div>
							)}
						</div>
					))}
				</div>
			</div>

			{/* Contact Form */}
			<div className="bg-card border border-border rounded-lg p-6">
				<h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
					<Icon name="MessageSquare" size={20} />
					Contact Support
				</h2>

				<form onSubmit={handleSubmit} className="space-y-4">
					<div>
						<label className="block text-sm font-medium text-foreground mb-2">
							Subject
						</label>
						<input
							type="text"
							value={contactForm.subject}
							onChange={(e) =>
								setContactForm({
									...contactForm,
									subject: e.target.value,
								})
							}
							placeholder="What can we help you with?"
							className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
							required
						/>
					</div>

					<div>
						<label className="block text-sm font-medium text-foreground mb-2">
							Message
						</label>
						<textarea
							value={contactForm.message}
							onChange={(e) =>
								setContactForm({
									...contactForm,
									message: e.target.value,
								})
							}
							placeholder="Describe your issue or question..."
							rows={6}
							className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
							required
						/>
					</div>

					<Button type="submit" className="w-full" disabled={isSubmitting}>
						<Icon name="Send" size={16} className="mr-2" />
						{isSubmitting ? "Sending..." : "Send Message"}
					</Button>
				</form>
			</div>
		</div>
	);
};

export default HelpPage;
